import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Typography, Button } from 'antd';
import { SyncOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  getEstado,
  getRitmo,
  type BrevoSyncEstadoResponse,
  type BrevoSyncRitmoPunto,
} from '../api/brevoSyncApi';
import StatusBanner from '../components/BrevoSync/StatusBanner';
import PhaseProgress from '../components/BrevoSync/PhaseProgress';
import StatCards from '../components/BrevoSync/StatCards';
import HistorySection from '../components/BrevoSync/HistorySection';
import LaunchModal from '../components/BrevoSync/LaunchModal';

const { Title } = Typography;

const BrevoSyncPage: React.FC = () => {
  const [estado, setEstado] = useState<BrevoSyncEstadoResponse | null>(null);
  const [ritmo, setRitmo] = useState<BrevoSyncRitmoPunto[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const isRunning = !!estado?.running;

  const fetchEstado = useCallback(async () => {
    try {
      const data = await getEstado();
      setEstado(data);
      setLoading(false);

      // Fetch ritmo si hay ejecución en curso
      if (data.running) {
        try {
          const ritmoData = await getRitmo(data.running.id, 'A');
          setRitmo(ritmoData.puntos);
        } catch { /* silently fail */ }
      }
    } catch {
      setLoading(false);
    }
  }, []);

  // Polling
  useEffect(() => {
    fetchEstado();

    const setupInterval = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      const delay = isRunning ? 5000 : 60000;
      intervalRef.current = setInterval(fetchEstado, delay);
    };

    setupInterval();

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, fetchEstado]);

  // Pausa polling cuando la pestaña pierde foco
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchEstado();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchEstado]);

  const handleLaunched = () => {
    // Polling agresivo los primeros 30s para detectar el running lo antes posible
    let attempts = 0;
    const poll = () => {
      fetchEstado();
      attempts++;
      if (attempts < 15) setTimeout(poll, 2000);
    };
    setTimeout(poll, 1000);
  };

  return (
    <div style={{ padding: '0 8px' }}>
      <div style={{ background: '#fff', padding: '16px 24px', borderRadius: 8 }}>
        {/* Cabecera */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
          <Title level={4} style={{ margin: 0 }}>Sincronización Auraquantic ↔ Brevo</Title>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button
              type="text"
              icon={<SettingOutlined />}
              onClick={() => navigate('/brevo-sync/sistemas')}
              size="small"
              style={{ color: '#999' }}
              aria-label="Panel de Sistemas"
            />
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={() => setModalVisible(true)}
              disabled={isRunning}
            >
              Lanzar sincronización
            </Button>
          </div>
        </div>

        {/* Banner de estado */}
        <StatusBanner estado={estado} ritmo={ritmo} loading={loading} />

        {/* Progreso por fases (solo si running) */}
        {estado?.running && <PhaseProgress ejecucion={estado.running} />}

        {/* Cards de métricas */}
        <StatCards
          ejecucion={estado?.running}
          ultimaFinalizada={estado?.ultimaFinalizada}
          ritmo={ritmo}
        />

        {/* Histórico */}
        <HistorySection />

        {/* Modal de lanzamiento */}
        <LaunchModal
          visible={modalVisible}
          hasRunning={isRunning}
          onClose={() => setModalVisible(false)}
          onLaunched={handleLaunched}
        />
      </div>
    </div>
  );
};

export default BrevoSyncPage;
